import {
  FluidAddressFields,
  FluidFieldTranslations,
  FluidLocale,
  FluidTranslationSet,
} from '@lmig/fluid-core';

import {
  FluidAddressFormConfig,
  FluidAddressFormConfiguration,
} from './fluid-address-form-config.class';
import { defaultAddressConfig } from './locale-config/default/default-address.config';
import { frAddressConfig } from './locale-config/fr/fr-address.config';
import { gbAddressConfig } from './locale-config/gb/gb-address.config';
import { usAddressConfig } from './locale-config/us/us-address.config';

/**
 * Utility function to merge translations
 * @param config
 * @param translations
 */
export const mergeTranslations = (
  config: FluidAddressFormConfiguration,
  translations: FluidTranslationSet<FluidFieldTranslations<FluidAddressFields>>
) => {
  return {
    ...config,
    translations: { ...translations, ...config.translations },
  } as FluidAddressFormConfig;
};

/**
 * Returns a specific address form configuration for the given locale, if one
 * exists, otherwise, returns the default configuration (US format, but with no
 * lookups)
 * @param country
 * @param fieldAlias
 * @param translations
 */
export const addressFormLocale = (
  country: FluidLocale,
  fieldAlias: any,
  translations: any
) => {
  // Step 5 - Integrate new locale forms here.
  return (
    {
      [FluidLocale.UNITED_KINGDOM]: gbAddressConfig({
        fieldAlias,
        translations,
      }),
      [FluidLocale.UNITED_STATES]: usAddressConfig({
        fieldAlias,
        translations,
      }),
      [FluidLocale.FRANCE]: frAddressConfig({
        fieldAlias,
        translations,
      }),
    }[country] || defaultAddressConfig({ fieldAlias, translations })
  );
};

/**
 * Defaults to use when none are provided by user.
 */
export const defaultAddressConfiguration: FluidAddressFormConfiguration = {
  fieldAlias: {
    addressTitle: 'addressTitle',
    addressLineOne: 'addressLineOne',
    addressLineTwo: 'addressLineTwo',
    townOrCity: 'townOrCity',
    country: 'country',
    countyOrProvince: 'countyOrProvince',
    postOrZipCode: 'postOrZipCode',
    regionOrLocality: 'regionOrLocality',
  },
};

type Obj = { [key: string]: any };

export function deepMerge(obj1: Obj, obj2: Obj): Obj {
  const result: Obj = {};

  for (const key in obj1) {
    if (Object.prototype.hasOwnProperty.call(obj1, key)) {
      const val1 = obj1[key];
      const val2 = obj2[key];

      if (typeof val2 === 'object' && val2 !== null) {
        result[key] = deepMerge(val1, val2);
      } else {
        result[key] = val2 !== undefined ? val2 : val1;
      }
    }
  }

  for (const key in obj2) {
    if (
      Object.prototype.hasOwnProperty.call(obj2, key) &&
      obj1[key] === undefined
    ) {
      result[key] = obj2[key];
    }
  }

  return result;
}
